import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, CheckCheck } from 'lucide-react'
import { api } from '../../utils/api'
import { CellState, Frequency, Status, Rules, STATE_STYLE, Legend, errMsg, fmtDay } from './shared'

interface Cell { s: CellState; k?: string; r?: string | null; by?: string | null; at?: string }
interface Summary { ok: number; issue: number; missing: number; excluded: number; required: number; checked: number; completeness: number | null }
interface GridItem {
  id: string; name: string; isHost: boolean; checkable: boolean; parentId: string | null; statusSetId: string
  activeFrom: string | null; activeTo: string | null; cells: Record<string, Cell>; summary: Summary
}
export interface Grid {
  group: { id: string; name: string; frequency: Frequency; kind: string; statusSetId: string }
  today: string; month: string; periods: string[]; statusSets: Record<string, Status[]>
  items: GridItem[]; summary: Summary; rules: Rules
}
interface HistoryRow { id: string; action: string; username: string | null; createdAt: string; before?: any; after?: any; summary?: string }

const DOW = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']

function colHeader(freq: Frequency, p: string) {
  if (freq === 'DAILY') return { top: String(parseInt(p.slice(8))), sub: DOW[new Date(`${p}T00:00:00Z`).getUTCDay()] }
  if (freq === 'WEEKLY') return { top: p.slice(8), sub: p.slice(5, 7) + '/' }
  return { top: p.slice(5, 7), sub: 'เดือน' }
}

function CellDialog({ grid, item, period, canRecord, onClose }: {
  grid: Grid; item: GridItem; period: string; canRecord: boolean; onClose: () => void
}) {
  const qc = useQueryClient()
  const cell = item.cells[period]
  const statuses = grid.statusSets[item.statusSetId] ?? []
  const [remark, setRemark] = useState(cell?.r ?? '')
  const [err, setErr] = useState('')
  const editable = canRecord && cell.s !== 'na' && cell.s !== 'future'

  const { data: history = [] } = useQuery<HistoryRow[]>({
    queryKey: ['check-history', item.id, period],
    queryFn: () => api.get('/checks/records/history', { params: { itemId: item.id, date: period } }).then(r => r.data),
  })
  const save = useMutation({
    mutationFn: (statusKey: string | null) => api.put('/checks/records', { itemId: item.id, date: period, statusKey, remark: statusKey ? remark : null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['check-grid'] }); qc.invalidateQueries({ queryKey: ['check-dashboard'] }); onClose() },
    onError: e => setErr(errMsg(e)),
  })
  const st = STATE_STYLE[cell.s]
  const label = (k?: string) => statuses.find(s => s.key === k)?.label ?? k ?? '-'

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-gray-900">{item.name}</p>
            <p className="text-xs text-gray-500">{grid.group.name} · {fmtDay(period)} {period.slice(0, 4)}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-3">
          <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg" style={{ background: st.bg, color: st.fg }}>
            <b>{cell.k ? label(cell.k) : st.glyph}</b> {st.label}
          </span>
          {cell.s === 'na' && <p className="text-xs text-gray-500">ช่องนี้อยู่นอกช่วงใช้งานของรายการ ({item.activeFrom ?? '…'} ถึง {item.activeTo ?? 'ปัจจุบัน'}) จึงไม่นับเป็นการตรวจไม่ครบ</p>}
          {cell.by && <p className="text-xs text-gray-500">บันทึกโดย {cell.by} · {cell.at && new Date(cell.at).toLocaleString('th-TH')}</p>}
          {cell.r && !editable && <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2 whitespace-pre-wrap">{cell.r}</p>}
          {editable && (
            <>
              <div className="flex flex-wrap gap-2">
                {statuses.map(s => (
                  <button key={s.key} disabled={save.isPending} onClick={() => save.mutate(s.key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold text-white border-2 ${cell.k === s.key ? 'border-gray-900' : 'border-transparent'}`}
                    style={{ background: s.color }} title={s.kind === 'ok' ? 'ผ่าน' : s.kind === 'issue' ? 'ผิดปกติ' : 'ยกเลิก/ปิด'}>
                    {s.kind === 'ok' ? '✓ ' : s.kind === 'issue' ? '▲ ' : '⊘ '}{s.label}
                  </button>
                ))}
                {cell.k && <button disabled={save.isPending} onClick={() => save.mutate(null)} className="px-3 py-1.5 rounded-lg text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">ล้างผล</button>}
              </div>
              <textarea value={remark} onChange={e => setRemark(e.target.value)} rows={2} maxLength={1000} placeholder="Remark (ระบุเมื่อผิดปกติ)"
                className="w-full border border-gray-200 rounded-lg p-2 text-sm"/>
              {cell.k && <button disabled={save.isPending} onClick={() => save.mutate(cell.k!)} className="text-xs text-blue-600 hover:underline">บันทึกเฉพาะ remark</button>}
              {err && <p className="text-xs text-red-600">{err}</p>}
            </>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">ประวัติการแก้ไข</p>
            {history.length === 0 ? <p className="text-xs text-gray-400">ยังไม่มีประวัติ</p> : (
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {history.map(h => (
                  <li key={h.id} className="text-xs text-gray-600 border-l-2 border-gray-200 pl-2">
                    <span className="text-gray-400">{new Date(h.createdAt).toLocaleString('th-TH')}</span> · {h.username}
                    <br/>{h.before && <>{label(h.before.statusKey)} → </>}{h.after ? label(h.after.statusKey) : 'ล้างผล'}
                    {h.after?.remark && <span className="text-gray-500"> — "{h.after.remark}"</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Monthly grid: item × period. Used for recording (canRecord) and as the dashboard heatmap (read-only). */
export default function CheckGrid({ groupId, month, canRecord, compact = false }: {
  groupId: string; month: string; canRecord: boolean; compact?: boolean
}) {
  const qc = useQueryClient()
  const [sel, setSel] = useState<{ itemId: string; period: string } | null>(null)
  const [bulkKey, setBulkKey] = useState('')
  const { data: grid, isLoading, error } = useQuery<Grid>({
    queryKey: ['check-grid', groupId, month],
    queryFn: () => api.get('/checks/grid', { params: { groupId, month } }).then(r => r.data),
    enabled: !!groupId,
  })
  const bulk = useMutation({
    mutationFn: (p: { date: string; statusKey: string }) => api.post('/checks/records/bulk', { groupId, ...p }),
    onSuccess: r => { qc.invalidateQueries({ queryKey: ['check-grid'] }); qc.invalidateQueries({ queryKey: ['check-dashboard'] }); alert(`บันทึกให้ ${r.data.created} รายการ`) },
    onError: e => alert(errMsg(e)),
  })

  if (isLoading) return <p className="text-sm text-gray-400 py-8 text-center">กำลังโหลด…</p>
  if (error || !grid) return <p className="text-sm text-red-500 py-8 text-center">โหลดข้อมูลไม่สำเร็จ</p>
  if (!grid.items.length) return <p className="text-sm text-gray-400 py-8 text-center">Group นี้ยังไม่มีรายการ — เพิ่มได้ที่แท็บ "จัดการรายการ"</p>

  const freq = grid.group.frequency
  const mainSet = grid.statusSets[grid.group.statusSetId] ?? []
  const todayPeriod = [...grid.periods].reverse().find(p => p <= grid.today)
  const selItem = sel ? grid.items.find(i => i.id === sel.itemId) : null
  const cw = compact ? 'w-7 h-7' : 'w-8 h-8'

  const rows: { item: GridItem; host: boolean }[] = []
  const tops = grid.items.filter(i => !i.parentId)
  for (const t of tops) {
    rows.push({ item: t, host: t.isHost })
    if (t.isHost) grid.items.filter(c => c.parentId === t.id).forEach(c => rows.push({ item: c, host: false }))
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <Legend items={(['ok', 'issue', 'missing', 'na', 'excluded'] as CellState[]).map(s => ({ glyph: STATE_STYLE[s].glyph, label: STATE_STYLE[s].label, bg: STATE_STYLE[s].bg, fg: STATE_STYLE[s].fg }))}/>
        {canRecord && todayPeriod && (
          <div className="flex items-center gap-1.5">
            <select value={bulkKey} onChange={e => setBulkKey(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs">
              <option value="">บันทึกทั้ง Group…</option>
              {mainSet.filter(s => s.kind === 'ok').map(s => <option key={s.key} value={s.key}>ทุกรายการ = {s.label}</option>)}
            </select>
            <button disabled={!bulkKey || bulk.isPending}
              onClick={() => confirm(`บันทึก "${bulkKey}" ให้ทุกรายการที่ยังไม่ได้ตรวจของ ${fmtDay(todayPeriod)} ?`) && bulk.mutate({ date: todayPeriod, statusKey: bulkKey })}
              className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white rounded-lg text-xs disabled:opacity-40"><CheckCheck size={13}/> {fmtDay(todayPeriod)}</button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto border border-gray-100 rounded-xl">
        <table className="text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 z-10 bg-gray-50 text-left px-3 py-1.5 min-w-[160px] max-w-[220px] font-semibold text-gray-600">รายการ</th>
              {grid.periods.map(p => {
                const h = colHeader(freq, p)
                return <th key={p} className={`px-0.5 py-1 font-medium ${p === grid.today ? 'text-blue-700' : 'text-gray-500'}`}><div>{h.top}</div><div className="text-[10px] text-gray-400">{h.sub}</div></th>
              })}
              <th className="px-2 py-1 font-semibold text-gray-600 whitespace-nowrap" title={grid.rules.completeness}>% ครบ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ item, host }) => (host && !item.checkable) ? (
              <tr key={item.id} className="bg-slate-50">
                <td colSpan={grid.periods.length + 2} className="sticky left-0 px-3 py-1 font-semibold text-slate-700">▸ {item.name}{item.activeTo && <span className="ml-2 text-[10px] font-normal text-gray-400">(ยกเลิกใช้งาน {item.activeTo})</span>}</td>
              </tr>
            ) : (
              <tr key={item.id} className={`border-t border-gray-50 ${host ? 'bg-slate-50/70' : ''}`}>
                <td className={`sticky left-0 z-10 ${host ? 'bg-slate-50' : 'bg-white'} px-3 py-0.5 truncate max-w-[220px] ${item.parentId ? 'pl-6' : ''} ${item.activeTo ? 'text-gray-400' : host ? 'font-semibold text-slate-700' : 'text-gray-800'}`} title={item.name}>
                  {host ? '▸ ' : ''}{item.name}{item.activeTo && <span className="ml-1 text-[10px]">(ยกเลิก)</span>}
                </td>
                {grid.periods.map(p => {
                  const c = item.cells[p]
                  const stat = mainSet.length ? (grid.statusSets[item.statusSetId] ?? []).find(s => s.key === c.k) : undefined
                  const stl = STATE_STYLE[c.s]
                  const bg = stat && (c.s === 'ok' || c.s === 'issue') ? stat.color : stl.bg
                  return (
                    <td key={p} className="p-0.5">
                      <button onClick={() => setSel({ itemId: item.id, period: p })} disabled={c.s === 'future' && !canRecord}
                        title={`${item.name} · ${fmtDay(p)} · ${stat?.label ?? stl.label}${c.r ? ` — ${c.r}` : ''}`}
                        aria-label={`${item.name} ${p} ${stl.label}`}
                        className={`${cw} rounded font-bold text-[11px] flex items-center justify-center relative ${c.s === 'future' ? 'cursor-default' : 'hover:ring-2 hover:ring-blue-400'}`}
                        style={{ background: bg, color: stl.fg }}>
                        {stat && c.k ? stat.label.slice(0, 3) : stl.glyph}
                        {c.r && <span className="absolute top-0 right-0 w-1.5 h-1.5 rounded-full bg-white border border-gray-500" aria-label="มี remark"/>}
                      </button>
                    </td>
                  )
                })}
                <td className="px-2 text-center whitespace-nowrap" title={`${item.summary.checked}/${item.summary.required} · ผ่าน ${item.summary.ok} · ผิดปกติ ${item.summary.issue} · ยังไม่ได้ตรวจ ${item.summary.missing}`}>
                  {item.summary.completeness == null ? '–' : `${item.summary.completeness}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        รวม: ผ่าน <b>{grid.summary.ok}</b> · ผิดปกติ <b>{grid.summary.issue}</b> · ยังไม่ได้ตรวจ <b>{grid.summary.missing}</b> · ครบถ้วน <b>{grid.summary.completeness ?? '–'}{grid.summary.completeness != null && '%'}</b>
        <span className="text-gray-400"> — {grid.rules.completeness}</span>
      </p>

      {sel && selItem && <CellDialog grid={grid} item={selItem} period={sel.period} canRecord={canRecord} onClose={() => setSel(null)}/>}
    </div>
  )
}
